<script setup>
import { ref, watch } from 'vue';
import { shortenNumber } from '@utils/';

const myChart = ref(null);

const props = defineProps({
    data: {
        type: Array,
        default: [],
    },
});

const generateChartOptions = (data) => {
    return {
        tooltip: {
            trigger: 'item',
        },
        series: [
            {
                type: 'pie',
                radius: ['70%', '95%'],
                avoidLabelOverlap: false,
                padAngle: 2,
                itemStyle: {
                    borderRadius: 8,
                },
                label: {
                    show: false,
                    position: 'center',
                    formatter: ({ percent, name, value }) => {
                        return ['{percent|' + parseInt(percent) + '%}', '{title|' + name + '}', '{value|' + shortenNumber(value) + '}'].join('\n');
                    },
                    rich: {
                        percent: {
                            align: 'center',
                            fontSize: 36,
                            fontWeight: 'bold',
                            textBorderWidth: 0,
                            lineHeight: 60,
                            color: (params) => params.color,
                        },
                        title: {
                            fontWeight: 'bold',
                            fontSize: 14,
                            textBorderWidth: 0,
                        },
                        value: {
                            align: 'center',
                            fontSize: 14,
                            textBorderWidth: 0,
                            lineHeight: 30,
                            color: (params) => params.color,
                        },
                    },
                },
                tooltip: {
                    show: false,
                    trigger: 'item',
                    formatter: '{b}: {c} ',
                },
                emphasis: {
                    label: {
                        show: true,
                        fontSize: 24,
                        fontWeight: 'bold',
                    },
                },
                labelLine: {
                    show: false,
                },
                data: data,
            },
        ],
    };
};

const reactiveChartOptions = ref(generateChartOptions(props.data));
watch(
    () => props.data,
    (newData) => {
        reactiveChartOptions.value = generateChartOptions(newData);
    }
);

const highlightSegment = (index) => {
    if (myChart.value) {
        myChart.value.dispatchAction({
            type: 'highlight',
            seriesIndex: 0,
            dataIndex: index,
        });
    }
};

const resetHighlight = () => {
    if (myChart.value) {
        myChart.value.dispatchAction({
            type: 'downplay',
            seriesIndex: 0,
        });
    }
};

const getSegmentColor = (index) => {
    if (myChart.value) {
        const instance = myChart.value;
        const color = instance.getOption().color[index];
        return color;
    }
    return '#000';
};
</script>

<template>
    <div class="pie-wrapper-container">
        <VueECharts ref="myChart" :option="reactiveChartOptions" style="width: 280px; height: 280px; flex: none"> </VueECharts>
        <ul class="pie-list">
            <li v-for="(item, index) in props.data" :key="item.name" @mouseover="highlightSegment(index)" @mouseout="resetHighlight" class="chart-item">
                <span class="dot" :style="{ backgroundColor: getSegmentColor(index) }"> </span>
                <div class="pie-list-item">
                    {{ item.name }} <span class="pie-percent">{{ shortenNumber(item.value) }}</span>
                </div>
            </li>
        </ul>
    </div>
</template>

<style scoped>
.dot {
    display: inline-block;
    width: 16px;
    height: 16px;
    border-radius: 50%;
    margin-right: 5px;
}

.pie-list {
    display: flex;
    gap: 4px;
    flex-direction: column;
    width: 100%;
}

.pie-list-item {
    display: flex;
    justify-content: space-between;
    width: 100%;
}

.chart-item {
    font-size: 16px;
    font-weight: 400;
    display: flex;
    align-items: center;
    padding: 4px 8px;
    border-radius: 8px;
    user-select: none;
}

.chart-item:hover {
    background: var(--surface-hover);
}

.pie-wrapper-container {
    display: flex;
    gap: 16px;
    align-items: center;
    width: 100%;
}

.pie-percent {
    font-weight: bold;
}
@media (max-width: 768px) {
    .pie-wrapper-container {
        @apply flex flex-col gap-4;
    }
}

@media (max-width: 1024px) {
}
</style>
